import React, { useEffect, useState } from 'react';
import { Body, Box, BoxProps, Icon } from '@hover/blueprint';
import { motion } from '@hover/blueprint/foundation';
import { iChevronDown } from '@hover/icons';
import styled from '@emotion/styled';
import { IDropdownItem } from '../../../interfaces/IDropdownItem';
import useClickOutSide from '../../../hooks/useClickOutside';

const StyledDropdownItem = styled(Box)`
  &:hover {
    div {
      width: 4px;
      transition: all 0.2s ease-in;
    }
  }
`;

interface Props extends BoxProps {
  items: Array<IDropdownItem>;
  multiSelect?: boolean;
  onChangeValue: (selectedValues: Array<IDropdownItem>) => void;
  placeholder?: string;
  errorMessage?: string;
  value?: Array<IDropdownItem>;
}

const Dropdown: React.FC<Props> = ({
  items,
  multiSelect,
  onChangeValue,
  placeholder,
  errorMessage,
  value,
  ...restProps
}) => {
  const [selectedItems, setSelectedItems] = useState<Array<IDropdownItem>>([]);
  const [isOpen, setIsOpen] = useState<boolean>(false);

  useEffect(() => {
    if (!value) {
      setSelectedItems([]);
    }
  }, [value]);

  const toggleDropdown = () => setIsOpen(!isOpen);
  const { componentRef } = useClickOutSide(setIsOpen);

  const getPlaceHolder = () => {
    if (selectedItems.length) {
      if (multiSelect) {
        const keys = selectedItems.map(si => si.value);
        return keys.join(', ');
      }
      return selectedItems[0].value;
    }
    return placeholder?.toUpperCase();
  };

  const onItemClick = (item: IDropdownItem) => {
    if (multiSelect) {
      const isExist = selectedItems.find(
        selected => selected.value === item.value,
      );
      if (isExist) {
        const newValues = selectedItems.filter(
          selected => selected.value !== item.value,
        );
        setSelectedItems(newValues);
        onChangeValue(newValues);
      } else {
        const newValues = [...selectedItems, item];
        setSelectedItems(newValues);
        onChangeValue(newValues);
      }
    } else {
      setSelectedItems([item]);
      setIsOpen(false);
      onChangeValue([item]);
    }
  };

  return (
    <Box
      ref={componentRef}
      w="100%"
      h={400}
      position="relative"
      borderRadius="4px"
      justifyContent="space-between"
      alignItems="center"
      {...restProps}
    >
      <Box
        borderRadius="4px"
        as="button"
        cursor="pointer"
        borderWidth="1px"
        borderColor={errorMessage ? 'orange.500' : 'neutral.300'}
        _focus={{
          borderColor: errorMessage ? 'brandOrange.500' : 'primary.700',
          boxShadow: errorMessage ? 'haloDanger' : 'haloPrimary',
        }}
        onClick={toggleDropdown}
        display="flex"
        alignItems="center"
        justifyContent="space-between"
        w="100%"
        px={400}
        h="100%"
      >
        <Box flexDir="column" alignItems="flex-start">
          {selectedItems.length ? (
            <Body
              size={200}
              isBold
              color={errorMessage ? 'orange.600' : 'neutral.600'}
            >
              {placeholder}
            </Body>
          ) : (
            <></>
          )}
          <Body
            overflow="hidden"
            maxW="250px"
            whiteSpace="nowrap"
            textOverflow="ellipsis"
            size={selectedItems.length ? 500 : 300}
            fontWeight={selectedItems.length ? 600 : 700}
            letterSpacing="0.1rem"
            color={selectedItems.length ? 'primary.900' : 'brandNavy.300'}
          >
            {getPlaceHolder()}
          </Body>
        </Box>
        <Icon strokeWidth="2px" size="default" m={0} icon={iChevronDown} />
      </Box>
      <Box
        borderRadius="8px"
        boxShadow="distance300"
        zIndex={100}
        position="absolute"
        top="56px"
        bg="neutral.0"
        left={0}
        w="100%"
        maxH={isOpen ? '265px' : 0}
        transition={`all ${motion.easings[250]}`}
        flexDir="column"
        overflow="auto"
        py={isOpen ? 300 : 0}
      >
        <Box minH={300} maxH={300} px={600}>
          <Body color="brandNavy.300">Select your state</Body>
        </Box>
        {items.map((item: IDropdownItem) => (
          <StyledDropdownItem
            _hover={{
              bg: 'primary.100',
            }}
            px={600}
            cursor="pointer"
            onClick={() => onItemClick(item)}
            minH={300}
            maxH={300}
            w="100%"
            alignItems="center"
            key={item.key}
            position="relative"
            color="primary.900"
            background={
              selectedItems.find(it => it === item)
                ? 'primary.100'
                : 'transparent'
            }
          >
            <Box
              bg="primary.600"
              w={selectedItems.find(it => it === item) ? '4px' : 0}
              position="absolute"
              top={0}
              left={0}
              bottom={0}
            />
            {item.value}
          </StyledDropdownItem>
        ))}
      </Box>
    </Box>
  );
};

Dropdown.defaultProps = {
  multiSelect: false,
  placeholder: 'STATE',
  errorMessage: '',
};

export default Dropdown;
